import { fetchApi } from "./api";
import { API_ENDPOINTS } from "../config/api";
import type {
  ReviewQueueItem,
  ReviewQueueCounts,
  ReviewQueueParams,
  MarkReviewedRequest,
  SetReviewStatusRequest,
  SetReviewStatusResponse,
  ChangeSummary,
  ProductReviewOverview,
  ReviewEntityType,
  StaleGroup,
  BulkResolveRequest,
  BulkResolveResponse,
  ReviewMatrixResponse,
  MatrixResolveRequest,
  MatrixResolveResponse,
} from "../types/review";

const ALL_REVIEW_STATUSES: ReviewQueueItem["review_status"][] = [
  "unreviewed",
  "stale",
  "in_progress",
  "reviewed",
  "canceled",
];

export async function getReviewQueue(
  params: ReviewQueueParams = {},
): Promise<ReviewQueueItem[]> {
  const search = new URLSearchParams();
  const statuses =
    params.status == null
      ? ALL_REVIEW_STATUSES
      : Array.isArray(params.status)
        ? params.status.filter((s): s is ReviewQueueItem["review_status"] =>
            ALL_REVIEW_STATUSES.includes(s),
          )
        : [params.status];
  for (const status of statuses) {
    search.append("status", status);
  }
  if (params.source) search.set("source", params.source);
  if (params.entity_type) search.set("entity_type", params.entity_type);
  if (params.product_id != null)
    search.set("product_id", String(params.product_id));
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.reviewsQueue}?${query}`
    : API_ENDPOINTS.reviewsQueue;
  return fetchApi<ReviewQueueItem[]>(url);
}

/** Same params as getReviewQueue; returns items with group-preserving pagination (groups never split across pages). */
export async function getReviewQueueGrouped(
  params: ReviewQueueParams = {},
): Promise<ReviewQueueItem[]> {
  const search = new URLSearchParams();
  if (params.status != null) {
    const statuses = Array.isArray(params.status)
      ? params.status.filter((s): s is ReviewQueueItem["review_status"] =>
          ALL_REVIEW_STATUSES.includes(s),
        )
      : [params.status];
    for (const status of statuses) {
      search.append("status", status);
    }
  }
  if (params.source) search.set("source", params.source);
  if (params.entity_type) search.set("entity_type", params.entity_type);
  if (params.product_id != null)
    search.set("product_id", String(params.product_id));
  if (params.limit != null) search.set("limit", String(params.limit));
  if (params.offset != null) search.set("offset", String(params.offset));
  if (params.has_open_issues != null)
    search.set("has_open_issues", String(params.has_open_issues));
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.reviewsQueueGrouped}?${query}`
    : API_ENDPOINTS.reviewsQueueGrouped;
  return fetchApi<ReviewQueueItem[]>(url);
}

export async function getReviewQueueCount(
  source?: string,
): Promise<ReviewQueueCounts> {
  const url = source
    ? `${API_ENDPOINTS.reviewsQueueCount}?source=${encodeURIComponent(source)}`
    : API_ENDPOINTS.reviewsQueueCount;
  return fetchApi<ReviewQueueCounts>(url);
}

export async function setProductReviewStatus(
  productId: number,
  body: SetReviewStatusRequest,
): Promise<SetReviewStatusResponse> {
  const payload: SetReviewStatusRequest = {
    review_type: "direct",
    ...body,
  };
  return fetchApi<SetReviewStatusResponse>(
    API_ENDPOINTS.reviewProductStatus(productId),
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function setVariantReviewStatus(
  variantId: number,
  body: SetReviewStatusRequest,
): Promise<SetReviewStatusResponse> {
  const payload: SetReviewStatusRequest = {
    review_type: "direct",
    ...body,
  };
  return fetchApi<SetReviewStatusResponse>(
    API_ENDPOINTS.reviewVariantStatus(variantId),
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function markProductReviewed(
  productId: number,
  body: MarkReviewedRequest,
): Promise<SetReviewStatusResponse> {
  return setProductReviewStatus(productId, { ...body, status: "reviewed" });
}

export async function markVariantReviewed(
  variantId: number,
  body: MarkReviewedRequest,
): Promise<SetReviewStatusResponse> {
  return setVariantReviewStatus(variantId, { ...body, status: "reviewed" });
}

export async function markProductInProgress(
  productId: number,
  body: MarkReviewedRequest,
): Promise<SetReviewStatusResponse> {
  return setProductReviewStatus(productId, { ...body, status: "in_progress" });
}

export async function markVariantInProgress(
  variantId: number,
  body: MarkReviewedRequest,
): Promise<SetReviewStatusResponse> {
  return setVariantReviewStatus(variantId, { ...body, status: "in_progress" });
}

export async function getProductChanges(
  productId: number,
): Promise<ChangeSummary | null> {
  const data = await fetchApi<ChangeSummary | null>(
    API_ENDPOINTS.reviewProductChanges(productId),
  );
  return data;
}

export async function getVariantChanges(
  variantId: number,
): Promise<ChangeSummary | null> {
  const data = await fetchApi<ChangeSummary | null>(
    API_ENDPOINTS.reviewVariantChanges(variantId),
  );
  return data;
}

export async function getProductOverview(
  productId: number,
): Promise<ProductReviewOverview> {
  return fetchApi<ProductReviewOverview>(
    API_ENDPOINTS.reviewProductOverview(productId),
  );
}

const BULK_PAGE_SIZE = 200;

/** Fetch all queue items for given params by paginating (e.g. for bulk review by source). */
export async function getReviewQueueAll(params: {
  source: string;
  status?: ReviewQueueItem["review_status"];
  entity_type?: ReviewQueueItem["entity_type"];
}): Promise<ReviewQueueItem[]> {
  const items: ReviewQueueItem[] = [];
  let offset = 0;
  let page: ReviewQueueItem[];
  do {
    page = await getReviewQueue({
      ...params,
      limit: BULK_PAGE_SIZE,
      offset,
    });
    items.push(...page);
    offset += page.length;
  } while (page.length === BULK_PAGE_SIZE);
  return items;
}

/** Get stale review groups for a source, optionally filtered by entity_type. */
export async function getStaleGroups(params: {
  source: string;
  entity_type?: ReviewEntityType;
}): Promise<StaleGroup[]> {
  const search = new URLSearchParams();
  if (params.entity_type) search.set("entity_type", params.entity_type);
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.reviewStaleGroups(params.source)}?${query}`
    : API_ENDPOINTS.reviewStaleGroups(params.source);
  return fetchApi<StaleGroup[]>(url);
}

/** Bulk-resolve all stale reviews matching entity_type + changed_fields for a source. */
export async function bulkResolveStaleGroup(
  source: string,
  body: BulkResolveRequest,
): Promise<BulkResolveResponse> {
  return fetchApi<BulkResolveResponse>(
    API_ENDPOINTS.reviewBulkResolve(source),
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export async function getSourceReviewMatrix(
  sourceId: string,
  productIds?: number[],
): Promise<ReviewMatrixResponse> {
  const search = new URLSearchParams();
  if (productIds && productIds.length > 0) {
    for (const id of productIds) {
      search.append("product_ids", String(id));
    }
  }
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.reviewMatrix(sourceId)}?${query}`
    : API_ENDPOINTS.reviewMatrix(sourceId);
  return fetchApi<ReviewMatrixResponse>(url);
}

export async function resolveSourceReviewMatrix(
  sourceId: string,
  body: MatrixResolveRequest,
): Promise<MatrixResolveResponse> {
  return fetchApi<MatrixResolveResponse>(
    API_ENDPOINTS.reviewMatrixResolve(sourceId),
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export interface GroupReviewResponse {
  success: boolean;
  reviewed_variant_ids?: number[];
  indirectly_covered_variant_ids?: number[];
}

export async function setVariantReviewGroupStatus(
  variantId: number,
  body: SetReviewStatusRequest,
): Promise<GroupReviewResponse> {
  return fetchApi<GroupReviewResponse>(
    API_ENDPOINTS.reviewVariantGroupStatus(variantId),
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}
